'use strict';

var AppState = require('app_state');
var getPrettyAxisLabels = require('utils/charts/charts_axis').getPrettyAxisLabels;
var Log = require('utils/log');
var Misc = require('utils/misc');
var StandardTileView = require('components/dashboard/views/standard_tile_view');
var SVGUtils = require('utils/svg');

var colors = {
    // copied from variables.less
    blueLight: '#9cbad3',
    blue: '#70a9d9',
    blueDark: '#5682a6',
    tealLight: '#b2dcdb',
    teal: '#a1edec',
    tealDark: '#7eb9b8',
    orangeLight: '#f4c3a8',
    orange: '#ff8a3b',
    orangeDark: '#e98850',
    purpleLight: '#cec8f6',
    purple: '#9c90ed',
    purpleDark: '#8c7aff',
    roseLight: '#eed1e4',
    rose: '#ffa8e1',
    roseDark: '#dea2c9',
    beigeLight: '#ddd2c7',
    beige: '#edd0b4',
    beigeDark: '#bba48e',
    redLight: '#e8aaaa',
    red: '#ff6161',
    redDark: '#d15555',
    yellowLight: '#f6edaf',
    yellow: '#ffe645',
    yellowDark: '#eedb5f',
    greenLight: '#a3e3b6',
    green: '#57fa88',
    greenDark: '#46c86d',
    grayLight: '#a2beba',
    gray: '#6c7d87',
    grayDark: '#434e54',
    success: '#00b84c',
    warning: '#f0d000',
    danger: '#ff0808',

    grayNeutral: '#ddd', // added because there is no "true" gray in the SAMSON chart color palette
    custom: AppState.style[1],
};

var DiagramType = {
    BAR: 'bar',
    DONUT: 'donut',
    HISTOGRAM: 'histogram',
    PIE: 'pie',
};

var DiagramTypeList = [];
_.each(DiagramType, function (value, _key) {
    DiagramTypeList.push(value);
});

var X_MAX_LABEL_COUNT = 10;
var Y_MAX_LABEL_COUNT = 8;
var X_AXIS_WIDTH = 200;
var Y_AXIS_HEIGHT = 100;

var getFormattedValue = function (unit, value, total) {
    if (unit === '%') {
        return Math.round((value / total) * 100);
    }

    var formatedValue = Misc.getValueDisplayInfos(value, 'MBR', undefined, unit, undefined, false);
    if (isNaN(formatedValue.value)) {
        return '--';
    }
    var fixedDecimals = formatedValue.value.toFixed(1);

    return fixedDecimals.endsWith('.0') ? formatedValue.value : fixedDecimals;
};

var PieChartTile = StandardTileView.extend({
    className: function () {
        var chartOptions = this.options.customisation;
        var centerGraphic = !chartOptions.showLegend || chartOptions.legendPosition === 'bottom';
        return centerGraphic ? 'grid-chart-container centered' : 'grid-chart-container';
    },

    getTemplate: function () {
        switch (this.options.type) {
            case DiagramType.PIE:
            case DiagramType.DONUT:
                return (this.template = require('components/dashboard/templates/svg_piechart'));
            case DiagramType.BAR:
            case DiagramType.HISTOGRAM:
                return (this.template = require('components/dashboard/templates/svg_barchart'));
            default:
                return require('components/common/templates/message');
        }
    },

    ui: {
        bar: 'rect.js-chart-bar',
    },

    events: {
        'mouseover @ui.bar': 'showInfo',
        'mouseleave @ui.bar': 'hideInfo',
    },

    showInfo: function (e) {
        var id = e.currentTarget.id.split('-', 2)[1];
        SVGUtils.addClass($(e.currentTarget), 'brighter');
        SVGUtils.removeClass(this.ui['info-' + id], 'hidden');
        SVGUtils.removeClass(this.ui['x-label-' + id], 'darker');
        this.oldFontSize = this.ui['x-label-' + id].attr('font-size');
        this.ui['x-label-' + id].attr('font-size', '10px');
    },

    hideInfo: function (e) {
        var id = e.currentTarget.id.split('-', 2)[1];
        SVGUtils.removeClass($(e.currentTarget), 'brighter');
        SVGUtils.addClass(this.ui['info-' + id], 'hidden');
        SVGUtils.addClass(this.ui['x-label-' + id], 'darker');
        this.ui['x-label-' + id].attr('font-size', this.oldFontSize);
    },

    initialize: function (options) {
        // create a unique suffix for ids in the chart to ensure unique ids over the dashboard
        this.uid = Date.now();
        Log.assert(DiagramTypeList.includes(this.options.type));

        if (options.customisation.legendPosition === 'bottom') {
            this.className += ' centered';
        }

        for (var idx = 0; idx < 45; idx++) {
            this.ui['info-' + idx] = '#info-' + idx + '-' + this.uid;
            this.ui['x-label-' + idx] = '.info-' + idx;
        }
    },

    serializeData: function () {
        switch (this.options.type) {
            case DiagramType.PIE:
            case DiagramType.DONUT:
                return $.extend({ uid: this.uid }, this.getPieChartConfig(this.options.type === DiagramType.DONUT));
            case DiagramType.BAR:
            case DiagramType.HISTOGRAM:
                return $.extend({ uid: this.uid }, this.getBarConfig(this.options.type));
            default:
                return {
                    color_class: 'box-color-error',
                    title: i18n.TR('Fehler'),
                    titleID: 'dashboard-chart-error',
                    text: i18n.TR('Unbekannter Diagrammtyp'),
                };
        }
    },

    getBarConfig: function (type) {
        var self = this;

        var c = this.options.customisation;
        var isHistogram = type === DiagramType.HISTOGRAM;
        Log.assertEach(c.colors || [], function (entry) {
            return colors[entry] != undefined;
        });

        var bars = this.options.buckets;
        var lowerBound = this.options.min;
        var upperBound = this.options.max;
        var xLabels = {};
        var barSep = isHistogram ? 1 : 3;

        // Temperaturs come with a factor 10 from the backend, so we have to multiply with 0.1
        var unitFactorX = c.unit === '°C' && isHistogram ? 0.1 : 1;
        var unitFactorY = c.unit === '°C' && !isHistogram ? 0.1 : 1;

        if (isHistogram) {
            xLabels = getPrettyAxisLabels(
                lowerBound * unitFactorX,
                upperBound * unitFactorX,
                X_MAX_LABEL_COUNT,
                function (val, min, max) {
                    return (X_AXIS_WIDTH / (max - min)) * (val - min);
                },
                // If the histogram's x-axis' unit is '%', than it will always range from 0 to 100, so
                // we have to set the last parameter of getFormattedValue to 100.
                function (val) {
                    return getFormattedValue(c.unit || self.options.meta.unit, val, 100);
                },
                c.xLabelDensity
            );
        } else {
            c.colors = c.colors || _.keys(colors);
            bars = [];
            _.each(this.options.data, function (entry) {
                bars.push(entry.value);
            });
            var idx = 0;
            xLabels = {
                labels: _.map(this.options.data, function (entry) {
                    var label = {
                        value: entry.name,
                        position: (idx + 0.5) * (X_AXIS_WIDTH / bars.length),
                        color: colors[c.colors[idx]] || c.colors[idx],
                        rotation: c.labelRotation === undefined ? 35 : c.labelRotation,
                        fontSize: bars.length > 12 ? '5px' : '8px',
                    };
                    idx += 1;
                    return label;
                }),
            };
        }
        var maxBarHeight = _.max(bars);
        var barCount = bars.length;

        var configuration = {
            bars: [],
            // The barWidth is calculated to ensure all bars fit within the diagram's range,
            // excluding the two additional bars at the start and end in histograms,
            // which are placed outside the histogram and not considered in the width calculation.
            barWidth: X_AXIS_WIDTH / (barCount - (isHistogram ? 2 : 0)) - barSep,
            isHistogram: isHistogram,
            y: getPrettyAxisLabels(
                _.min(bars) * unitFactorY,
                _.max(bars) * unitFactorY,
                Y_MAX_LABEL_COUNT,
                function (val, min, max) {
                    return (100 * (max - val)) / (max - min);
                },
                function (val) {
                    return getFormattedValue(isHistogram ? '' : c.unit, val, 1);
                },
                c.yLabelDensity
            ),
            x: xLabels,
            uid: this.uid,
        };

        function formatLabel(idx) {
            var label = ((lowerBound + (idx * (upperBound - lowerBound)) / (barCount - 2)) * unitFactorX).toFixed(1);
            if (label.endsWith('.0')) {
                label = label.substring(0, label.length - 2);
            }
            return label;
        }

        // create hover labels. Hovering over a bar will display its (x|y)-value.
        for (var barIdx = 0; barIdx < barCount; barIdx++) {
            var xHoverLabel = formatLabel(barIdx);
            // for a histogram, the x-label shall be a range like [20,30]
            if (isHistogram) {
                var xSecondValue = xHoverLabel;
                xHoverLabel = formatLabel(barIdx - 1);
                switch (barIdx) {
                    case 0:
                        // special case: The first bar contains all values smaller than min
                        // if it is empty, omit it
                        if (this.options.buckets[0] === 0) {
                            continue;
                        }
                        xHoverLabel = '< ' + xSecondValue;
                        break;
                    case barCount - 1:
                        // special case: The last bar contains all values greater than max
                        // if it is empty, omit it
                        if (this.options.buckets[barCount - 1] === 0) {
                            continue;
                        }
                        xHoverLabel = '> ' + xHoverLabel;
                        break;
                    default:
                        xHoverLabel = '[' + xHoverLabel + ', ' + xSecondValue + ']';
                }
            }

            configuration.bars.push({
                x: (isHistogram ? 0 : 3) + (barIdx - (isHistogram ? 1 : 0)) * (configuration.barWidth + barSep),
                y: Y_AXIS_HEIGHT - (bars[barIdx] * 100) / maxBarHeight,
                height: Math.max(1, (bars[barIdx] * 100) / maxBarHeight),
                color: isHistogram ? colors[c.color] || colors.custom : colors[c.colors[barIdx]] || c.colors[barIdx],

                // data for the hover effect
                info: {
                    x: {
                        position: (barIdx + 0.5) * configuration.barWidth - 15,
                        value: xHoverLabel,
                    },
                    y: {
                        position: Y_AXIS_HEIGHT - (bars[barIdx] * 100) / maxBarHeight - 8,
                        value: bars[barIdx] * unitFactorY,
                    },
                },
            });
        }

        return $.extend(configuration, c);
    },

    checkConfig: function (c) {
        var validLegendPositions = ['bottom', 'right', undefined];
        Log.assert(validLegendPositions.includes(c.legendPosition));
        Log.assert(typeof c.showLegend === 'boolean' || c.showLegend === undefined);
        Log.assert(typeof c.centerText === 'string' || c.centerText === undefined);
        Log.assert(typeof c.unit === 'string' || c.unit === undefined);

        Log.assert(typeof this.options.data === 'object');
        Log.assert(typeof this.options.meta === 'object' || this.options.meta === undefined);
        Log.assert(this.options.meta.unit === undefined || typeof this.options.meta.unit === 'string');
        Log.assertEach(this.options.data, function (entry) {
            return typeof entry.name === 'string' && typeof entry.value === 'number';
        });
    },

    getPieChartConfig: function (isDonut) {
        var c = this.options.customisation;
        this.checkConfig(c);

        c.colors = c.colors || colors;

        var total = _.reduce(
            this.options.data,
            function (sum, entry) {
                return sum + entry.value;
            },
            0
        );

        // no values at all? -> show a circle witho no values
        if (total === 0) {
            total = 1;
            this.options.data = [{ value: 1, name: '' }];
            c.centerText = '--';
            c.showLegend = false;
        }

        // makes the first pie-piece start at the top of the circle instead of the right
        var offsetAngle = -90;
        var radius = isDonut ? 25 : 15;
        var perimeter = 2 * radius * Math.PI;

        var piePieces = [];

        var idx = 0;
        _.each(
            this.options.data,
            function (entry) {
                var value = entry.value;
                var name = entry.name;
                var fraction = value / total;
                Log.assert(fraction <= 1 && fraction >= 0);

                piePieces.push({
                    name: name,
                    displayValue: getFormattedValue(c.unit || this.options.meta.unit, value, total),
                    color: colors[c.colors[idx]] || c.colors[idx],
                    strokeWidth: isDonut ? 8 : 30,
                    radius: radius,

                    sortValue: value,

                    angle: offsetAngle,
                    filledShare: fraction * perimeter,
                    emptyShare: (1 - fraction) * perimeter,
                });

                offsetAngle += fraction * 360;
                idx++;
            },
            this
        );

        var ctFct = c.centerTextFunction;
        if (
            ctFct &&
            window.SDE_plugins &&
            window.SDE_plugins.dashboard &&
            typeof window.SDE_plugins.dashboard[ctFct] === 'function'
        ) {
            // use function from plugin
            c.centerText = window.SDE_plugins.dashboard[ctFct](this.options.data, c);
        } else {
            // function is either undefined or not available -> look for default definitions
            switch (ctFct) {
                case 'getOnlineGateways':
                    if (this.options.data.length != 2) {
                        c.centerText = '--';
                    } else {
                        var online = this.options.data[0].value;
                        var offline = this.options.data[1].value;
                        c.centerText = ((online / (offline + online)) * 100).toFixed(1) + ' %';
                    }
                    break;
                default:
                    // center function undefined
                    Log.assert(false);
            }
        }

        return $.extend({ piePieces: piePieces }, c);
    },
});

module.exports = {
    getTileView: function (data) {
        return new PieChartTile(data);
    },
};
